import { useEffect, useRef, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate, useNavigationType } from "react-router-dom";
import axios from "axios";
import CookieConsent from "@/components/CookieConsent";
import SessionSync from "@/components/SessionSync";
import { Toaster } from "@/components/ui/sonner";
import { resolveLanding } from "@/lib/roleContext";

// Pages
import LandingPage from "@/pages/LandingPage";
import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import DashboardNew from "@/pages/DashboardNew";
import NewUserLanding from "@/pages/NewUserLanding";
import IPOFaqPage from "@/pages/IPOFaqPage";
import IPORoleSelect from "@/pages/IPORoleSelect";
import CAFirmOnboarding from "@/pages/CAFirmOnboarding";
import CAFirmDashboard from "@/pages/CAFirmDashboard";
import RegisteredWorkspace from "@/pages/RegisteredWorkspace";
import RegisteredAdvisor from "@/pages/RegisteredAdvisor";
import AdvisorProfile from "@/pages/AdvisorProfile";
import AdvisorPremium from "@/pages/AdvisorPremium";
import IPOCostEstimator from "@/pages/IPOCostEstimator";
import IPOWorkspaceOnboarding from "@/pages/IPOWorkspaceOnboarding";
import IPONavigator from "@/pages/IPONavigator";
import AdvisorMarketplace from "@/pages/AdvisorMarketplace";
import PublishedProjects from "@/pages/PublishedProjects";
import AdvisorMarketplaceExperts from "@/pages/AdvisorMarketplaceExperts";
import AdvisorOnboardingLogin from "@/pages/AdvisorOnboardingLogin";
import DocumentVault from "@/pages/DocumentVault";
import DRHPWorkspace from "@/pages/drhp-workspace/DRHPWorkspace";
import LegalDisclaimer from "@/pages/LegalDisclaimer";
import TermsOfUse from "@/pages/TermsOfUse";
import AdminCenter from "@/pages/AdminCenter";
import OraclePage from "@/pages/OraclePage";
import OracleFloatingButton from "@/components/OracleFloatingButton";
import HomeButton from "@/components/HomeButton";
import DeepTechComingSoon from "@/pages/DeepTechComingSoon";
import AdminLogin from "@/pages/AdminLogin";
import AccessDenied from "@/pages/AccessDenied";
import AccountDetails from "@/pages/AccountDetails";
import ResourcesPage from "@/pages/ResourcesPage";
import AdvisorsPage from "@/pages/AdvisorsPage";
import PricingPage from "@/pages/PricingPage";
import ResellerNetworkPage from "@/pages/ResellerNetworkPage";
import DisclaimerPage from "@/pages/DisclaimerPage";
import AboutPage from "@/pages/AboutPage";
import CareersPage from "@/pages/CareersPage";
import Payments from "@/pages/Payments";
import MarketAnalytics from "@/pages/MarketAnalytics";
import FundingPlatform from "@/pages/FundingPlatform";

// Components

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance with credentials
const apiClient = axios.create({
  baseURL: API,
  withCredentials: true
});

// Auth Context
export const AuthContext = ({ children }) => {
  return children;
};

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionId = hash.split("session_id=")[1]?.split("&")[0];
      // New-vs-Existing intent carried through the OAuth redirect (?flow=new|existing)
      const params = new URLSearchParams(window.location.search);
      const flow = params.get("flow") || "";
      // Optional post-auth destination (e.g. ?next=advisor for the Advisor login).
      const next = params.get("next") || "";

      if (!sessionId) {
        navigate("/login");
        return;
      }

      try {
        const response = await apiClient.post("/auth/session", {
          session_id: sessionId,
          intent: flow,
        });

        // Clear the hash from URL
        window.history.replaceState(null, "", window.location.pathname);

        const u = response.data.user;

        // Advisor Google login: route by whether an advisor profile exists.
        if (next === "advisor") {
          let hasProfile = false;
          try {
            const p = await apiClient.get("/matchmaker/expert/my-profile");
            hasProfile = !!(p.data && p.data.profile);
          } catch { /* ignore */ }
          if (hasProfile) { navigate("/advisor-marketplace", { state: { user: u } }); }
          else { navigate("/advisor-onboarding?ob=needprofile", { state: { user: u } }); }
          return;
        }

        // Admins → full dashboard; returning role users → their last-active
        // dashboard; brand-new users → the New User Landing Page.
        const dest = await resolveLanding(u);
        navigate(dest, { state: { user: u } });
      } catch (error) {
        console.error("Auth failed:", error);
        const status = error?.response?.status;
        if (next === "advisor") {
          if (status === 409) { navigate("/advisor-onboarding?ob=exists"); return; }
          if (status === 404) { navigate("/advisor-onboarding?ob=notfound"); return; }
          navigate("/advisor-onboarding?ob=error"); return;
        }
        if (status === 409) { navigate("/login?notice=exists"); return; }
        if (status === 404) { navigate("/login?notice=notfound"); return; }
        navigate("/login");
      }
    };

    processAuth();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="flex flex-col items-center gap-4">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        <p className="text-muted-foreground text-sm">Authenticating...</p>
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, showFooter = true }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(
    location.state?.user ? true : null
  );
  const [user, setUser] = useState(location.state?.user || null);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await apiClient.get("/auth/me");
        setUser(response.data);
        setIsAuthenticated(true);
      } catch (error) {
        // If auth/me fails but we have location state, try using that
        if (location.state?.user) {
          setUser(location.state.user);
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
          navigate("/login");
        }
      }
    };

    checkAuth();
  }, [location.state, navigate]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  // Pass user and apiClient to children
  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1">
        {children({ user, apiClient })}
      </div>
      <HomeButton user={user} />
      <OracleFloatingButton />
      <CookieConsent user={user} />
    </div>
  );
};

// Admin-Only Route — STRICT: /dashboard-new is reachable ONLY by admins.
// Always verifies with the server (/auth/me) and never trusts client-passed
// state. Any non-admin (new/registered) — via back, logo or any button — is
// bounced to /new-user-landing. Unauthenticated users go to /login.
const AdminRoute = ({ children }) => {
  const [status, setStatus] = useState("checking"); // checking | admin | denied | unauth
  const [user, setUser] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await apiClient.get("/auth/me");
        if (!active) return;
        if (res.data?.is_admin) { setUser(res.data); setStatus("admin"); }
        else setStatus("denied");
      } catch {
        if (active) setStatus("unauth");
      }
    })();
    return () => { active = false; };
  }, []);

  if (status === "checking") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground text-sm">Verifying access...</p>
        </div>
      </div>
    );
  }
  if (status === "unauth") return <Navigate to="/login" replace />;
  if (status === "denied") return <Navigate to="/home" replace />;

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1">
        {children({ user, apiClient })}
      </div>
      <OracleFloatingButton />
      <CookieConsent user={user} />
    </div>
  );
};

// Admin Gate - checks existing session first, handles OAuth callback, then shows admin login if needed
const AdminGate = () => {
  const [adminUser, setAdminUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  const checkAdminStatus = async () => {
    try {
      const res = await apiClient.get("/auth/me");
      if (res.data.is_admin) {
        setAdminUser(res.data);
        return true;
      }
    } catch {}
    return false;
  };

  useEffect(() => {
    const init = async () => {
      // Handle OAuth callback on /admin route
      if (location.hash?.includes("session_id=")) {
        const sessionId = location.hash.split("session_id=")[1]?.split("&")[0];
        if (sessionId) {
          try {
            await apiClient.post("/auth/session", { session_id: sessionId });
            window.history.replaceState(null, "", "/admin");
          } catch {}
        }
      }
      await checkAdminStatus();
      setChecking(false);
    };
    init();
  }, []);

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground text-sm">Verifying admin access...</p>
        </div>
      </div>
    );
  }

  if (!adminUser) {
    return (
      <AdminLogin
        onLoginSuccess={async () => {
          const ok = await checkAdminStatus();
          if (!ok) {
            // Re-check after a brief delay for cookie propagation
            setTimeout(async () => { await checkAdminStatus(); }, 500);
          }
        }}
      />
    );
  }

  return <AdminCenter user={adminUser} apiClient={apiClient} />;
};

// App Router Component
const AppRouter = () => {
  const location = useLocation();

  // Check for session_id in hash FIRST (synchronous, before useEffect)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/resources" element={<ResourcesPage />} />
      <Route path="/advisors" element={<AdvisorsPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/reseller-network" element={<ResellerNetworkPage />} />
      <Route path="/funding" element={<FundingPlatform />} />
      <Route
        path="/payments"
        element={
          <ProtectedRoute>
            {({ user, apiClient }) => <Payments user={user} apiClient={apiClient} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/market-analytics"
        element={
          <ProtectedRoute>
            {({ user, apiClient }) => <MarketAnalytics user={user} apiClient={apiClient} />}
          </ProtectedRoute>
        }
      />
      <Route path="/__dash-new-preview" element={<DashboardNew />} />
      <Route path="/disclaimer" element={<DisclaimerPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/careers" element={<CareersPage />} />
      <Route path="/login" element={<Login apiClient={apiClient} />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/access-denied" element={<AccessDenied />} />
      <Route
        path="/dashboard-new"
        element={
          <AdminRoute>
            {({ user, apiClient }) => <DashboardNew user={user} apiClient={apiClient} />}
          </AdminRoute>
        }
      />
      <Route
        path="/home"
        element={
          <ProtectedRoute>
            {({ user, apiClient }) => <NewUserLanding user={user} apiClient={apiClient} />}
          </ProtectedRoute>
        }
      />
      <Route path="/new-user-landing" element={<Navigate to="/home" replace />} />
      <Route path="/ipo-faq" element={<IPOFaqPage />} />
      <Route
        path="/ipo-role-select"
        element={
          <ProtectedRoute>
            {() => <IPORoleSelect />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/ca-firm-onboarding"
        element={
          <ProtectedRoute>
            {() => <CAFirmOnboarding />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/ca-firm-dashboard"
        element={
          <ProtectedRoute>
            {() => <CAFirmDashboard />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/registered-workspace"
        element={
          <ProtectedRoute>
            {() => <RegisteredWorkspace />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/registered-advisor"
        element={
          <ProtectedRoute>
            {() => <RegisteredAdvisor />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/advisor/:expertId"
        element={
          <ProtectedRoute>
            {() => <AdvisorProfile />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/advisor-premium"
        element={
          <ProtectedRoute>
            {() => <AdvisorPremium />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/ipo-cost-estimator"
        element={
          <ProtectedRoute>
            {() => <IPOCostEstimator />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/ipo-workspace-onboarding"
        element={
          <ProtectedRoute>
            {() => <IPOWorkspaceOnboarding />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/ipo-navigator"
        element={
          <ProtectedRoute>
            {() => <IPONavigator />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/advisor-marketplace"
        element={
          <ProtectedRoute>
            {() => <AdvisorMarketplace />}
          </ProtectedRoute>
        }
      />
      <Route path="/advisor-onboarding" element={<AdvisorOnboardingLogin />} />
      <Route
        path="/published-projects"
        element={
          <ProtectedRoute>
            {() => <PublishedProjects />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/advisor-marketplace/experts"
        element={
          <ProtectedRoute>
            {() => <AdvisorMarketplaceExperts />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/document-vault"
        element={
          <ProtectedRoute>
            {() => <DocumentVault />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/drhp-workspace"
        element={
          <ProtectedRoute>
            {() => <DRHPWorkspace />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/legal-disclaimer"
        element={
          <ProtectedRoute>
            {({ user, apiClient }) => <LegalDisclaimer user={user} apiClient={apiClient} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/terms-of-use"
        element={
          <ProtectedRoute>
            {({ user, apiClient }) => <TermsOfUse user={user} apiClient={apiClient} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={<AdminGate />}
      />
      <Route
        path="/account"
        element={
          <ProtectedRoute>
            {({ user, apiClient }) => {
              // STRICT 3-email allow-list (platform owners only).
              const ALLOW = [
                "founders.ipolabs@gmail.com",
                "ronraj2312@gmail.com",
                "neeraj@emergent.sh",
              ];
              const allowed = ALLOW.includes((user?.email || "").toLowerCase());
              if (!allowed) {
                return (
                  <div className="min-h-screen flex items-center justify-center bg-white">
                    <div className="text-center max-w-md px-6">
                      <h2 className="text-xl font-semibold text-gray-800 mb-2">Access Restricted</h2>
                      <p className="text-gray-500 text-sm">
                        Account Details is reserved for IPOLabs platform owners only.
                        If you believe you should have access, please contact support.
                      </p>
                    </div>
                  </div>
                );
              }
              return <AccountDetails user={user} apiClient={apiClient} />;
            }}
          </ProtectedRoute>
        }
      />
      <Route
        path="/oracle"
        element={
          <ProtectedRoute>
            {({ user, apiClient }) => <OraclePage user={user} apiClient={apiClient} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/deep-market-research"
        element={
          <ProtectedRoute>
            {({ user, apiClient }) => <MarketAnalytics user={user} apiClient={apiClient} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/drhp-intelligence"
        element={
          <ProtectedRoute>
            {({ user, apiClient }) => <DeepTechComingSoon user={user} apiClient={apiClient} variant="drhp" />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/fraud-risk-analytics"
        element={
          <ProtectedRoute>
            {({ user, apiClient }) => <DeepTechComingSoon user={user} apiClient={apiClient} variant="fraud" />}
          </ProtectedRoute>
        }
      />

      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
};

// Global browser Back/Forward guard.
// Rule: the browser Back and Forward buttons behave as normal ONLY when the user
// is logged in. When the user is NOT logged in, pressing Back or Forward routes
// them to the public home page ("/").
// How it works: React Router reports a "POP" navigation for genuine Back/Forward
// actions (in-app navigate() calls are PUSH/REPLACE). We only act on a POP that
// happens AFTER the app has performed at least one in-app navigation, so the
// initial/direct page load (also a POP) and direct/SEO links are never disrupted.
// Auth is read synchronously from the localStorage `session_token` mirror (set on
// every login, cleared on logout) — zero extra network calls or performance cost.
let appHasNavigated = false;
const BackForwardGuard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const navType = useNavigationType();
  useEffect(() => {
    if (navType === "PUSH" || navType === "REPLACE") { appHasNavigated = true; return; }
    if (!appHasNavigated) return; // initial/direct load — leave untouched
    let loggedIn = false;
    try { loggedIn = !!localStorage.getItem("session_token"); } catch (_) { loggedIn = false; }
    if (!loggedIn && location.pathname !== "/") {
      navigate("/", { replace: true });
    }
  }, [location, navType, navigate]);
  return null;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <BackForwardGuard />
        <SessionSync />
        <AppRouter />
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
